'use client';

import { customInputProps } from '@/app/entrar/customInputProps';
import {
  FloatingLabelPasswordInput,
  FloatingLabelTextInput,
} from '@/components/FloatingLabelComponents/FloatingLabelInput';
import useApi, { ErrorType } from '@/hooks/useApi/useApi';
import { useStore } from '@/stores/store';
import { User } from '@/typings/user';
import { setAxiosAuthentication } from '@/utils/axios';
import { shouldSkipLoggingForAuthError } from '@/utils/error';
import { UserRoles } from '@/utils/roles';
import { USER_TOKEN } from '@/utils/storageKeys';
import { Alert, Anchor, Button, Text, Title } from '@mantine/core';
import { isEmail, isNotEmpty, useForm } from '@mantine/form';
import { setCookie } from 'cookies-next';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

export type LoginFormInput = {
  email: string;
  password: string;
  confirmPassword?: string;
};

enum LoginErrorMessage {
  PendingSubscription = 'Waiting for payment confirmation',
  Unfinished = 'Waiting to complete the purchase',
  NoPassword = 'User does not have password',
  WrongPassword = 'Wrong password',
  UserNotFound = 'User not found',
  InactiveOrganization = 'User has inactive organizations',
}

function AuthErrorMessage({
  error,
  onCreatePassword,
}: {
  error: ErrorType;
  onCreatePassword: () => void;
}) {
  if (error.status !== 401 || !error.message) return 'Ocorreu um erro. Tente novamente';

  if (error.message.includes(LoginErrorMessage.NoPassword)) {
    return (
      <div className="flex flex-col">
        <p>Essa conta ainda não possui uma senha </p>
        <Anchor
          c="red"
          ml="auto"
          type="button"
          component="button"
          onClick={onCreatePassword}
          className="text-inherit w-max mx-auto"
        >
          Criar senha
        </Anchor>
      </div>
    );
  }

  if (error.message.includes(LoginErrorMessage.PendingSubscription)) {
    return 'Aguardado confirmação de pagamento. Tente novamente mais tarde.';
  }

  return 'E-mail ou senha inválida';
}

export default function LoginByEmail() {
  const { loading, error, post, setLoading } = useApi('auth');
  const updateData = useStore((store) => store.updateData);
  const setSelectedOrganization = useStore((store) => store.setSelectedOrganization);

  const router = useRouter();
  const searchParams = useSearchParams();

  const emailFromUrl = searchParams?.get('email') || '';
  const redirectUrl = searchParams?.get('redirect') || '';

  const form = useForm<Omit<LoginFormInput, 'cpf'>>({
    validateInputOnBlur: true,
    initialValues: { email: emailFromUrl, password: '' },
    validate: {
      email: isEmail('Você precisa digitar um e-mail válido'),
      password: isNotEmpty('Você precisa digitar uma senha'),
    },
  });

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  async function handleSubmit({ email, password }: { email: string; password: string }) {
    post<{ token: string; user: User }>(
      { email, password },
      {
        error: {
          message: 'Error on login',
          showNotification: false,
          shouldSkipLogging: shouldSkipLoggingForAuthError,
        },
        onSuccess: async (res) => {
          const { token, user } = res;

          // Keeps loading TRUE because we were having no navigation feedback after a successful login
          setLoading(true);

          localStorage.setItem(USER_TOKEN, token);

          const maxAge = 1000 * 60 * 60 * 24 * 365; // 1 year for now

          setCookie(USER_TOKEN, token, { maxAge });
          setAxiosAuthentication(token);
          updateData('user', () => user);
          const org = user.organizations?.[0]?.organization;
          if (org) {
            setSelectedOrganization(org);
          }
          const path = '/';
          await fetch(`/api/revalidate?path=${encodeURIComponent(path)}&layout=true`);
          const superOrgId = process.env.NEXT_PUBLIC_SUPER_ORG_ID;
          let defaultUrl = '/';
          if (org?.id && superOrgId && org.id === superOrgId) {
            defaultUrl = '/organizacoes';
          }

          if (user.organizations?.[0]?.role === UserRoles.attendant) {
            defaultUrl = '/conversas';
          }

          // Usar redirect URL se fornecido, senão ir para home
          const targetUrl = redirectUrl || defaultUrl;
          router.push(targetUrl);
          router.refresh();
        },
      },
    );
  }

  const email = form.values.email;

  const firstAccessUrl = email
    ? `/primeiro-acesso?email=${encodeURIComponent(emailFromUrl)}`
    : '/primeiro-acesso';
  const resetEmailUrl = email
    ? `/registrar-senha?email=${encodeURIComponent(emailFromUrl)}`
    : '/registrar-senha';

  return (
    <form className="flex h-full flex-col text-white" onSubmit={form.onSubmit(handleSubmit)}>
      <div>
        <Title order={1} ta="center">
          Bem vindo
        </Title>
        <Text ta="center" mb="lg">
          Entre com seu e-mail e senha
        </Text>
      </div>
      <div className="flex flex-col gap-4 animate-[fade-in_0.5s_ease-in]">
        <FloatingLabelTextInput
          {...customInputProps}
          {...form.getInputProps('email')}
          type="email"
          label="E-mail"
          autoComplete="email"
        />

        <FloatingLabelPasswordInput
          {...customInputProps}
          {...form.getInputProps('password')}
          label="Senha"
        />

        {error && (
          <Alert color="red" radius="md" p="sm" classNames={{ message: 'text-center text-white' }}>
            <AuthErrorMessage
              error={error}
              onCreatePassword={() => {
                router.push(firstAccessUrl);
              }}
            />
          </Alert>
        )}

        <Button type="submit" variant="filled" fullWidth loading={loading}>
          {loading ? 'Autenticando' : 'Entrar'}
        </Button>
        <Button
          component={Link}
          variant="subtle"
          color="white"
          fullWidth
          mt="md"
          href={firstAccessUrl}
        >
          Meu primeiro acesso
        </Button>
        <Button
          component={Link}
          variant="subtle"
          color="white"
          fullWidth
          mt="xs"
          href={resetEmailUrl}
        >
          Esqueci minha senha
        </Button>
      </div>
    </form>
  );
}
